import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { realtime, wireRealtime } from '@/realtime';
import { isGuest, useAuth } from '@/stores/auth';
import { useModeration } from '@/stores/moderation';
import { useChat } from '@/stores/chat';
import { requireFace } from '@/features/face/FaceGate';
import { toast } from '@/stores/toasts';
import { endpoints } from '@/api';
import { qk, useInvites } from '@/api/queries';
import { inviteErrorMessage } from '@/api/reasons';
import type { Invite } from '@/api/types';
import { signOut } from '@/lib/session';
import { messagePreview } from '@/features/chat/model';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { CountdownRing } from '@/components/ui/Countdown';
import { Icon } from '@/components/ui/Icon';
import { spring } from '@/lib/motion';
import { formatDateTime, localTimezone } from '@/lib/time';
import { isUsPath } from '@/features/us/mode';

/**
 * v6.2: remember the browser's timezone on the profile (once per sign-in,
 * when it changed) so date emails render in the reader's own time.
 */
export function TimezoneSync() {
  const me = useAuth((s) => s.me);
  const setMe = useAuth((s) => s.setMe);
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!me) return;
    const tz = localTimezone();
    if (!tz || tz === 'UTC' || me.timezone === tz || sent.current === `${me.id}|${tz}`) return;
    sent.current = `${me.id}|${tz}`;
    endpoints
      .updateMe({ timezone: tz })
      .then(setMe)
      .catch(() => undefined);
  }, [me, setMe]);
  return null;
}

/** Starts/stops the WS with the session and wires events into caches + navigation. */
export function RealtimeBridge() {
  const status = useAuth((s) => s.status);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loc = useLocation();
  const pathRef = useRef(loc.pathname);
  pathRef.current = loc.pathname;

  useEffect(() => {
    if (status !== 'authed') {
      realtime.stop();
      if (status === 'anon') useChat.getState().reset();
      return;
    }
    const off = wireRealtime(realtime, qc, {
      onMatchFound: (s) => {
        // /match runs its own "match found" moment; everywhere else (accepted invites) go straight in.
        const p = pathRef.current;
        if (p === '/match' || p === `/call/${s.id}`) return;
        navigate(`/call/${s.id}`);
      },
      onConnectionCreated: (c) => !isUsPath(pathRef.current) && toast.people(`You and ${c.user.display_name} are now friends`, 'Chat or call them any time from Friends.'),
      onFriendRequest: (r) => !isUsPath(pathRef.current) && toast.people(`${r.from.display_name} sent you a friend request`, 'Accept it in Friends.'),
      onChatMessage: (m, name) => {
        const p = pathRef.current;
        if (p.startsWith('/call/') || p.includes('/pod/') || p === '/match') return; // don't distract mid-call
        if (isUsPath(p)) return; // v15: inside the space, its own chat shows what's new
        if (m.deleted) return;
        const text = messagePreview(m);
        toast.people(name ? `${name}` : 'New message', text.length > 80 ? `${text.slice(0, 80)}…` : text);
      },
      onDateReminder: (inv) => {
        if (isUsPath(pathRef.current)) return;
        const other = inv.role === 'host' ? inv.guest : inv.host;
        toast.people(`Your date with ${other?.display_name ?? 'your match'} starts in 15 min`, 'Hop in whenever you’re both ready.');
      },
      onPlanUpdated: (plan) => {
        if (plan === 'plus') toast.success('Popin Plus is active', 'Filters are unlocked. Enjoy!');
        else toast.info('Your Plus subscription has ended', 'You’re on the free plan. Everything core stays free.');
      },
    });
    const offErr = realtime.on('error', (e) => {
      if (e?.message) toast.error(e.message);
    });
    realtime.start();
    return () => {
      off();
      offErr();
    };
  }, [status, qc, navigate]);

  return null;
}

/** Suspension/ban → blocking screen + sign out. Warning → modal. */
/** Underage at birthdate entry, or a banned Google identity (SPEC_V2 §1/§8). Respectful, final, no retry. */
export function AccountClosed({ reason, onDone }: { reason: 'underage' | 'account_banned'; onDone: () => void }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-ground px-6 py-10" role="alertdialog" aria-modal="true" aria-labelledby="closed-title" data-testid="account-closed">
      <div className="max-w-md text-center">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-fg text-ground">
          <Icon name="shield" size={30} />
        </span>
        <h1 id="closed-title" className="font-display text-[44px] leading-[0.92]">
          {reason === 'underage' ? 'Popin is for adults only' : 'This account can’t use Popin'}
        </h1>
        {reason === 'underage' ? (
          <>
            <p className="mt-4 text-[15px] text-fg">Thanks for being honest. Popin is only for people 18 and over, so we can’t open an account for you.</p>
            <p className="mt-3 text-sm text-muted">
              To keep younger people safe, this Google account is now closed and can’t be reopened with a different birthday. Nothing you entered is shown to anyone.
            </p>
            <p className="mt-3 text-sm text-muted">If you entered the wrong date by mistake, contact Popin support from the email on this Google account.</p>
          </>
        ) : (
          <p className="mt-4 text-[15px] text-muted">This Google account was closed for breaking our community guidelines. Bans are tied to the Google account and stay in place even if the account is deleted.</p>
        )}
        <Button className="mt-8" accent="paper" onClick={onDone} data-testid="account-closed-ok">
          OK
        </Button>
      </div>
    </div>
  );
}

export function ModerationGate() {
  const { blocked, warning, setBlocked, setWarning, closed, setClosed } = useModeration();
  const navigate = useNavigate();
  const signedOut = useRef(false);

  useEffect(() => {
    if ((blocked || closed) && !signedOut.current) {
      signedOut.current = true;
      void signOut();
    }
    if (!blocked && !closed) signedOut.current = false;
  }, [blocked, closed]);

  if (closed) {
    return (
      <AccountClosed
        reason={closed}
        onDone={() => {
          setClosed(null);
          setBlocked(null);
          navigate('/', { replace: true });
        }}
      />
    );
  }

  return (
    <>
      {blocked && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ground px-6" role="alertdialog" aria-modal="true" aria-labelledby="blocked-title" data-testid="moderation-blocked">
          <div className="max-w-md text-center">
            <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-danger/15 text-danger">
              <Icon name="shield" size={32} />
            </span>
            <h1 id="blocked-title" className="font-display text-[44px] leading-[0.92]">
              {blocked.action === 'ban' ? 'Your account has been banned' : 'Your account is suspended'}
            </h1>
            <p className="mt-4 text-fg">
              <span className="text-muted">Reason: </span>
              {blocked.reason || 'A violation of the community guidelines.'}
            </p>
            {blocked.until && (
              <p className="mt-2 text-fg">
                <span className="text-muted">Until: </span>
                {formatDateTime(blocked.until)}
              </p>
            )}
            <p className="mt-6 text-sm text-muted">
              You’ve been signed out. If you think this is a mistake, our moderators review every automatic suspension.
            </p>
            <Button
              className="mt-8"
              accent="paper"
              onClick={() => {
                setBlocked(null);
                navigate('/', { replace: true });
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}
      <Modal open={!!warning} onClose={() => setWarning(null)} title="A warning from our moderators" testId="moderation-warning">
        <p className="text-[15px] text-fg">{warning?.reason}</p>
        <p className="mt-3 text-sm text-muted">Please review the community guidelines. Repeated issues can lead to a suspension.</p>
        <Button className="mt-6" block accent="paper" onClick={() => setWarning(null)}>
          I understand
        </Button>
      </Modal>
    </>
  );
}

function IncomingInviteCard({ invite }: { invite: Invite }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const total = Math.max(1, Date.parse(invite.expires_at) - Date.parse(invite.created_at));

  const drop = () =>
    qc.setQueryData<{ incoming: Invite[]; outgoing: Invite[] }>(qk.invites, (p) => (p ? { ...p, incoming: p.incoming.filter((x) => x.id !== invite.id) } : p));

  const accept = async () => {
    if (busy) return;
    setBusy('accept');
    if (!(await requireFace('Quick face check before you join'))) {
      setBusy(null);
      return;
    }
    try {
      const r = await endpoints.acceptInvite(invite.id);
      qc.setQueryData(qk.session(r.session.id), r.session);
      drop();
      navigate(`/call/${r.session.id}`);
    } catch (e) {
      toast.error('Couldn’t join', inviteErrorMessage(e, invite.from.display_name));
      drop();
      void qc.invalidateQueries({ queryKey: qk.invites });
    } finally {
      setBusy(null);
    }
  };
  const decline = async () => {
    if (busy) return;
    setBusy('decline');
    try {
      await endpoints.declineInvite(invite.id);
    } catch {
      /* expired already — fine */
    }
    drop();
    setBusy(null);
  };

  useEffect(() => {
    const ms = Date.parse(invite.expires_at) - Date.now();
    const t = setTimeout(drop, Math.max(0, ms + 1500));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite.id, invite.expires_at]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24 }}
      transition={spring}
      role="alertdialog"
      aria-label={`${invite.from.display_name} wants to call`}
      data-testid="incoming-invite"
      className="pointer-events-auto w-full max-w-sm rounded-card bg-yellow p-4 text-night shadow-lift"
    >
      <div className="flex items-center gap-3">
        <Avatar avatar={invite.from.avatar} size={52} />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-night/70">Call from a friend</p>
          <p className="truncate font-display text-[26px] leading-none">{invite.from.display_name} wants to talk</p>
        </div>
        <CountdownRing deadline={invite.expires_at} totalMs={total} size={44} stroke={4} color="#1A1A1A" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outline" accent="paper" className="border-night/30 text-night hover:bg-night/10" onClick={decline} loading={busy === 'decline'} disabled={!!busy} data-testid="invite-decline">
          Not now
        </Button>
        <Button accent="paper" className="bg-night text-bone hover:bg-night/90" icon="phone" onClick={accept} loading={busy === 'accept'} disabled={!!busy} data-testid="invite-accept">
          Accept
        </Button>
      </div>
    </motion.div>
  );
}

/** Incoming rematch invites float above whatever screen you're on. */
export function InviteCenter() {
  const me = useAuth((s) => s.me);
  const loc = useLocation();
  const member = !!me?.onboarded && !isGuest(me);
  const invites = useInvites(member);
  const incoming = (invites.data?.incoming ?? []).filter((i) => i.status === 'pending' && Date.parse(i.expires_at) > Date.now() - 1000);
  if (!member || loc.pathname.startsWith('/call/')) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(76px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6 lg:items-end lg:pr-6">
      <AnimatePresence>
        {incoming.slice(0, 2).map((i) => (
          <IncomingInviteCard key={i.id} invite={i} />
        ))}
      </AnimatePresence>
    </div>
  );
}
